import { Spinner } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  BlueprintFormRequest,
  BlueprintFormRequestSchema,
  BlueprintQuoteScope,
  UserRoleEnum,
} from '@monetize/types/app';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdOpenInNew } from 'react-icons/md';
import { handleApiErrorToast } from '../../../api/axios';
import {
  useGetBlueprintById,
  useMarkQuoteAsBlueprint,
  useUpdateBlueprintQuote,
} from '../../../api/cpqService';
import {
  MAlert,
  MBox,
  MButton,
  MCenter,
  MCenterModal,
  MFormField,
  MIcon,
  MInput,
  MLink,
  MStack,
  MText,
  MTextarea,
} from '../../../components/Monetize';
import { MCustomSelect } from '../../../components/Monetize/MCustomSelect/MCustomSelect';
import { BLUEPRINT_QUOTE_SCOPE_OPTIONS } from '../../../constants/quotes';
import {
  getQuoteBaseRoute,
  SETTINGS_QUOTE_BLUEPRINTS,
} from '../../../constants/routes';
import { BLUEPRINT_ADMIN_ROLES } from '../../../services/acl/roles';
import { useAuth } from '../../../services/auth0';
import { useConfirmModal } from '../../../services/confirmModal';

interface BlueprintModalProps {
  quoteId?: string;
  blueprintId?: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  hideManageBlueprintsLink?: boolean;
}

export const BlueprintModal = ({
  quoteId,
  blueprintId,
  isOpen,
  onClose,
  onSuccess,
  hideManageBlueprintsLink = false,
}: BlueprintModalProps) => {
  const { showConfirmModal, setModalLoading, closeModal } = useConfirmModal();
  const { currentTenantUserHasRole, currentTenantUser } = useAuth();
  const isEditMode = !!blueprintId;

  const { data: blueprint, isLoading: isLoadingBlueprint } =
    useGetBlueprintById(blueprintId || '', {
      enabled: isEditMode && !!blueprintId,
    });

  const isAdmin = currentTenantUserHasRole(BLUEPRINT_ADMIN_ROLES);
  const isTenantAdmin = currentTenantUserHasRole([UserRoleEnum.TENANT_ADMIN]);
  const isSalesOnly =
    currentTenantUserHasRole([UserRoleEnum.SALES]) && !isAdmin;
  const canEditScope =
    (!isEditMode && isAdmin) ||
    (isEditMode &&
      isTenantAdmin &&
      blueprint?.createdBy === currentTenantUser?.id);

  const canEditBlueprint =
    !isEditMode ||
    (isEditMode && blueprint?.createdBy === currentTenantUser?.id) ||
    (isEditMode &&
      isTenantAdmin &&
      blueprint?.scope === BlueprintQuoteScope.TENANT);
  const isDisabled = !canEditBlueprint;

  const { mutate: markAsBlueprint, isPending: isMarking } =
    useMarkQuoteAsBlueprint({
      onSuccess: () => {
        onClose();
        onSuccess?.();
      },
      onError: (err) => {
        handleApiErrorToast(err);
      },
    });

  const { mutate: updateBlueprint, isPending: isUpdating } =
    useUpdateBlueprintQuote({
      onSuccess: () => {
        onClose();
        onSuccess?.();
      },
      onError: (err) => {
        handleApiErrorToast(err);
      },
    });

  const defaultScope = useMemo(
    () => (isAdmin ? BlueprintQuoteScope.TENANT : BlueprintQuoteScope.USER),
    [isAdmin],
  );
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<BlueprintFormRequest>({
    resolver: zodResolver(BlueprintFormRequestSchema),
    defaultValues: {
      name: '',
      description: '',
      scope: defaultScope,
    },
  });

  useEffect(() => {
    if (isEditMode && blueprint) {
      reset({
        name: blueprint.name,
        description: blueprint.description || '',
        isActive: blueprint.isActive,
        scope: blueprint.scope,
      });
    } else {
      reset({
        name: '',
        description: '',
        scope: defaultScope,
      });
    }
  }, [blueprint, isEditMode, reset, defaultScope]);

  const onSubmit = (data: BlueprintFormRequest) => {
    if (isEditMode && blueprintId) {
      updateBlueprint({
        blueprintId,
        payload: {
          name: data.name,
          description: data.description,
          isActive: data.isActive ?? true,
          scope: data.scope,
        },
      });
    } else if (quoteId) {
      markAsBlueprint({
        quoteId,
        payload: {
          name: data.name,
          description: data.description,
          scope: data.scope ?? defaultScope,
        },
      });
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const isPending = isMarking || isUpdating;

  return (
    <MCenterModal
      size="md"
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      modalTitle={isEditMode ? 'Edit Blueprint' : 'Use Quote as Blueprint'}
      renderFooter={() => (
        <MStack
          gap={4}
          direction="row"
          align="center"
          justify="space-between"
          flex={1}
        >
          <MStack gap={2} direction="row" align="center">
            {!hideManageBlueprintsLink && (
              <MLink
                href={SETTINGS_QUOTE_BLUEPRINTS}
                target="_blank"
                display="flex"
                alignItems="center"
                fontSize="sm"
              >
                Manage blueprints
                <MIcon as={MdOpenInNew} ml="1" color="tIndigo.base" />
              </MLink>
            )}
          </MStack>
          <MStack gap={4} direction="row">
            <MButton onClick={handleClose} variant="cancel" minW="auto">
              Cancel
            </MButton>
            {!isDisabled && (
              <MButton
                form="blueprint-form"
                isLoading={isPending}
                variant="primary"
                isDisabled={isLoadingBlueprint || !isValid}
                type="submit"
                minW="auto"
              >
                {isEditMode ? 'Save' : 'Create Blueprint'}
              </MButton>
            )}
          </MStack>
        </MStack>
      )}
    >
      {isLoadingBlueprint ? (
        <MCenter>
          <Spinner />
        </MCenter>
      ) : (
        <MBox>
          <form id="blueprint-form" onSubmit={handleSubmit(onSubmit)}>
            {!isEditMode && (
              <MAlert type="info" mb={4}>
                <MText fontSize="sm">
                  A Blueprint is a pre-configured quote template with offerings,
                  MSA, and settings. When creating new quotes, you can select
                  this blueprint to automatically apply the same configuration.
                </MText>
              </MAlert>
            )}
            {isEditMode && isDisabled && (
              <MAlert type="info" mb={4}>
                <MText fontSize="sm">
                  You are not authorized to edit this blueprint. You can only
                  edit blueprints you created.
                </MText>
              </MAlert>
            )}

            {blueprint?.quote?.id && (
              <MFormField label="Quote" mb={4}>
                <MLink
                  href={getQuoteBaseRoute(blueprint?.quote?.id)}
                  target="_blank"
                  display="flex"
                  alignItems="center"
                  fontSize="sm"
                >
                  {blueprint?.quote?.description}
                  <MIcon as={MdOpenInNew} ml="1" color="tIndigo.base" />
                </MLink>
              </MFormField>
            )}

            <MFormField
              label="Blueprint Name"
              error={errors.name}
              isRequired
              mb={4}
            >
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <MInput
                    {...field}
                    maxLength={80}
                    isDisabled={isDisabled}
                    variant={isDisabled ? 'readonly' : 'outline'}
                    isReadOnly={isDisabled}
                  />
                )}
              />
            </MFormField>

            <MFormField label="Description" mb={4}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <MTextarea
                    {...field}
                    value={field.value || ''}
                    rows={3}
                    maxLength={1000}
                    isDisabled={isDisabled}
                    variant={isDisabled ? 'readonly' : 'outline'}
                    isReadOnly={isDisabled}
                  />
                )}
              />
            </MFormField>

            <MFormField
              label="Available For"
              mb={4}
              tooltip={
                !canEditScope && isSalesOnly
                  ? 'You will be the only user able to view and use this blueprint.'
                  : undefined
              }
            >
              <Controller
                name="scope"
                control={control}
                render={({ field }) => (
                  <MCustomSelect
                    value={field.value ?? defaultScope}
                    onChange={(value) => field.onChange(value)}
                    items={BLUEPRINT_QUOTE_SCOPE_OPTIONS}
                    itemTitle="title"
                    itemValue="value"
                    isDisabled={!canEditScope}
                    isReadOnly={!canEditScope}
                    useMainInputAsSearchInput
                    clearable={false}
                  />
                )}
              />
            </MFormField>
          </form>
        </MBox>
      )}
    </MCenterModal>
  );
};
